import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Restaurant,
  History,
  People,
  Star,
  LocalDining,
  AccessTime,
} from '@mui/icons-material';
import { TeamMember, Milestone } from '../types';
import HeroSection from './HeroSection';

export default function AboutPage() {
  const teamMembers: TeamMember[] = [
    {
      name: 'Gudiya Sharma',
      role: 'Head Chef & Founder',
      description: 'With over 15 years of culinary experience, Gudiya brings authentic flavors and innovative recipes to every dish.',
      avatar: '👩‍🍳',
    },
    {
      name: 'Raj Kumar',
      role: 'Executive Chef',
      description: 'Specializing in fusion cuisine, Raj creates unique dishes that blend traditional techniques with modern flavors.',
      avatar: '👨‍🍳',
    },
    {
      name: 'Priya Singh',
      role: 'Restaurant Manager',
      description: 'Ensuring exceptional dining experiences for every guest with her attention to detail and warm hospitality.',
      avatar: '👩‍💼',
    },
  ];

  const milestones: Milestone[] = [
    {
      year: '2020',
      title: 'Restaurant Founded',
      description: 'Started with a small kitchen and a big dream to serve authentic food.',
    },
    {
      year: '2021',
      title: 'First Expansion',
      description: 'Expanded our seating capacity and added new menu items.',
    },
    {
      year: '2022',
      title: 'Award Winning',
      description: 'Received "Best New Restaurant" award from Food Critics Association.',
    },
    {
      year: '2023',
      title: 'Online Launch',
      description: 'Launched our online ordering system for delivery and takeout.',
    },
    {
      year: '2024',
      title: 'Community Recognition',
      description: 'Recognized for our community service and local sourcing initiatives.',
    },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Hero Section */}
      <HeroSection
        title="About Gudiya Cooking Recipe"
        description="Bringing authentic flavors and exceptional dining experiences to your table since 2020"
      />

      <Container maxWidth="lg">

        {/* Our Story */}
        <Paper sx={{ p: 6, mb: 8, borderRadius: 2 }}>
          <Typography variant="h3" fontWeight={600} align="center" gutterBottom>
            Our Story
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            Gudiya Cooking Recipe started as a small family kitchen with a passion for authentic, home-style cooking.
            What began as a dream to share our grandmother's recipes with the community has grown into a beloved
            restaurant that serves hundreds of satisfied customers every day.
          </Typography>
          <Typography variant="body1" paragraph align="center" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            We believe in using only the freshest ingredients, sourced locally whenever possible, and preparing
            each dish with the same care and attention that we would for our own family. Our commitment to quality
            and authenticity has made us a cornerstone of the local dining scene.
          </Typography>
        </Paper>

        {/* Values */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" fontWeight={600} align="center" gutterBottom>
            Our Values
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 64,
                    height: 64,
                    mx: 'auto',
                    mb: 2,
                    fontSize: 32,
                  }}
                >
                  <LocalDining />
                </Avatar>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Quality Ingredients
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We source the freshest, highest-quality ingredients from local farmers and trusted suppliers.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 64,
                    height: 64,
                    mx: 'auto',
                    mb: 2,
                    fontSize: 32,
                  }}
                >
                  <People />
                </Avatar>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Customer First
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your satisfaction is our top priority. We treat every customer like family.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 64,
                    height: 64,
                    mx: 'auto',
                    mb: 2,
                    fontSize: 32,
                  }}
                >
                  <Star />
                </Avatar>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Excellence
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We strive for excellence in everything we do, from cooking to service.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Timeline */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" fontWeight={600} align="center" gutterBottom>
            Our Journey
          </Typography>
          <Paper sx={{ p: 4 }}>
            <Grid container spacing={3}>
              {milestones.map((milestone, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
                        {milestone.year}
                      </Typography>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {milestone.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {milestone.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>

        {/* Team */}
        <Box>
          <Typography variant="h3" fontWeight={600} align="center" gutterBottom>
            Meet Our Team
          </Typography>
          <Grid container spacing={4}>
            {teamMembers.map((member, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      fontSize: 40,
                    }}
                  >
                    {member.avatar}
                  </Avatar>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    {member.name}
                  </Typography>
                  <Typography variant="h6" color="primary.main" gutterBottom>
                    {member.role}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {member.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
